import type { TrackReferenceOrPlaceholder } from '@livekit/components-react'
import { Track } from 'livekit-client'
import { t } from './i18n'
import type { MicPlatform } from './micHelp'
import { pickScreenShare } from './screenShare'
import { isTeacher } from './useClassroomSignals'

/**
 * Picture-in-picture policy: what the floating window is FOR, which video it
 * shows, and what this platform can actually do. Pure functions — the window
 * lifecycle lives in usePictureInPicture.ts.
 *
 * The product problem: a child who tabs away stops seeing the teacher, and a
 * teacher who opens lesson materials stops seeing the class — and on our config
 * the backgrounded tab also PAUSES remote video (adaptiveStream's
 * pauseVideoInBackground default), so "away" means frozen frames, not just a
 * hidden tab. A PiP'd element is livekit-client's own carve-out from that
 * pause (HTMLElementInfo.visible = isPiP || isIntersecting), which makes PiP
 * the honest fix rather than a gimmick: the one video that matters keeps
 * flowing precisely because it is in the floating window.
 */

/**
 * What this browser can do, probed at runtime (the build floor is Chrome 84,
 * so every path here is progressive enhancement):
 *
 * - 'document' — Document PiP (Chromium 116+, DESKTOP only): our own DOM in an
 *   always-on-top window, and the surface Chrome's video-conferencing
 *   auto-enter opens. The real product.
 * - 'video'    — element PiP (one <video> with browser chrome, manual entry).
 *   In practice: Android Chrome students, plus Safari 13.1+ via the SAME
 *   standard API — deliberately best-effort there (srcObject PiP was only
 *   fixed in Safari 17.2; earlier Safaris may refuse, we report and move on).
 * - 'none'     — no standard element PiP at all (old engines, some Firefox).
 */
export type PipMode = 'document' | 'video' | 'none'

export function detectPipMode(): PipMode {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 'none'
  if (window.documentPictureInPicture) return 'document'
  if (
    document.pictureInPictureEnabled &&
    typeof HTMLVideoElement !== 'undefined' &&
    typeof HTMLVideoElement.prototype.requestPictureInPicture === 'function'
  ) {
    return 'video'
  }
  return 'none'
}

/**
 * Which single video the floating window carries.
 *
 * Priority is the lesson, not the grid:
 *   1. the picked screen share — the SAME deterministic pick as the stage
 *      (own → teacher → lowest sid), so PiP and stage never disagree about
 *      which share is "the" share. A share YOU are giving is excluded: PiP of
 *      your own screen is a hall of mirrors, and for the presenter the lesson
 *      is the faces.
 *   2. the teacher's camera, for a student viewer — the teacher IS the class.
 *   3. STICKY previous focus — a teacher watching one student must not have the
 *      window hop between children on every re-render or speaker change; the
 *      current face stays until it actually leaves.
 *   4. first remote camera by identity — stable, so the initial pick does not
 *      churn either.
 *
 * Muted/unsubscribed cameras still qualify: the window then says "camera off"
 * or "paused to save data" honestly, exactly like the stage tiles do.
 */
interface AutoBlockedInputs {
  mode: PipMode
  hiddenCount: number
  autoEnterCount: number
}

/** When document auto-PiP has never fired after multiple tab hides, suspect a block. */
export function shouldSuspectAutoBlocked({
  mode,
  hiddenCount,
  autoEnterCount,
}: AutoBlockedInputs): boolean {
  return mode === 'document' && hiddenCount >= 2 && autoEnterCount === 0
}

/** Permission walkthrough for auto-PiP being blocked: Chromium or generic fallback. */
export function pipHelpSteps(platform: MicPlatform) {
  switch (platform) {
    case 'chromium-desktop':
      return [
        {
          text: t('pip.steps.chromium.1'),
          art: 'address-bar' as const,
          photo: 'chrome-desktop-site' as const,
        },
        {
          text: t('pip.steps.chromium.2'),
          art: 'site-settings' as const,
          photo: 'chrome-desktop-site' as const,
        },
      ]
    case 'edge-desktop':
      return [
        { text: t('pip.steps.chromium.1'), art: 'address-bar' as const },
        { text: t('pip.steps.chromium.2'), art: 'site-settings' as const },
      ]
    default:
      return [{ text: t('pip.steps.generic.1'), art: 'site-settings' as const }]
  }
}

export function pickPipFocus(
  tracks: TrackReferenceOrPlaceholder[],
  viewerIsTeacher: boolean,
  previous?: TrackReferenceOrPlaceholder,
): TrackReferenceOrPlaceholder | undefined {
  // Local tracks are excluded BEFORE the share pick, not after: pickScreenShare
  // deterministically prefers your own share, and rejecting its result later
  // would suppress a REMOTE share that is happily running during a two-share
  // race — the picker must never even see the local one. (CODEX-PIP Q6.1.)
  const remote = tracks.filter((track) => !track.participant.isLocal)
  const share = pickScreenShare(remote)
  if (share) return share

  const cameras = remote.filter((track) => track.source === Track.Source.Camera)
  if (!viewerIsTeacher) {
    const teacher = cameras.find((track) => isTeacher(track.participant.identity))
    if (teacher) return teacher
  }
  if (previous && previous.source === Track.Source.Camera) {
    const still = cameras.find(
      (track) => track.participant.identity === previous.participant.identity,
    )
    if (still) return still
  }
  return [...cameras].sort((a, b) =>
    a.participant.identity.localeCompare(b.participant.identity),
  )[0]
}
